import React, { useEffect, useState, useRef } from "react";
import { Button, Space, Table, Input, Tooltip, Modal, message, Row, Form, Col, Select, DatePicker } from "antd";
import type { TableColumnsType } from "antd";
import {
  EditOutlined,
  CarOutlined,
  SearchOutlined,
  FileDoneOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import "../../style/Live.css";
import CancelAssign from "../../components/CancelModel";
import CompleteModel from "../../components/Complete";
import ReassignModel from "../../components/ReassignModel";
import { styled } from "styled-components";
import { socket } from "../../utils/socket";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { autoCompleteRequest, autoCompleteReset, getLiveBookingsRequest } from "../../redux/actions/liveAction";
import { cancelBookingRequest, getSelectedCabRequest } from "../../redux/actions/newAction";
import { completeTripRequest, resetCompleteTrip } from "../../redux/actions/completeAction";
import dayjs from "dayjs";
import { Spin, TimePicker } from "antd";
import {
  getNewBookingEditRequest,
  getNewBookingEditReset,
  getLocationsRequest,
  getVehicleTypesRequest,
  getTripTypesRequest,
  getGoodsTypesRequest,
  getPackagesRequest,
  getIntercityListRequest,
  getNightFareRequest,
  resetFareEstimate,
  clearFareLists,
} from "../../redux/actions/bookingActions";
import { updateNewBookingRequest } from "../../redux/actions/newAction";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useMountEffectOnce } from "../../utils/hooks/useMountEffectOnce";
import { useLocationOptions } from "../../utils/hooks/useLocationOptions";
import LocationSelect from "../../components/LocationSelect";
import { isFlagOn } from "../../utils/bookingFlags";
import { COPY_CELL } from "../../utils/copyGuard";
import { getEmployeeName } from "../../utils/currentUser";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  [key: string]: any;
}

const { Search } = Input;

const LIVE_SOCKET_REFRESH_DEBOUNCE_MS = 400;

const pageWrapper: React.CSSProperties = {
  padding: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

const refreshBtn: React.CSSProperties = {
  height: 44,
  width: 44,
  borderRadius: 12,
  color: "#fff",
  border: "none",
  background: "#87398f",
};

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px clamp(10px, 1vw, 22px);
  border-radius: 16px;
  margin-bottom: 18px;
  background: rgba(137, 67, 154, 0.45);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  flex-wrap: nowrap;
  gap: clamp(10px, 1vw, 16px);

  /* Below antd lg one row stops being the right answer - fall back to the
     wrapping the toolbar has always done at these sizes. */
  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px;
    align-items: stretch;
  }
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  gap: clamp(8px, 1vw, 24px);

  /* Reassign / Complete / Cancel are a fixed 150px in Live.css and New.css.
     Scoped here so only this toolbar's copies shrink. */
  .action-btn1,
  .action-btn2 {
    width: clamp(96px, 8.5vw, 150px);
    min-width: 0;
    padding-inline: clamp(4px, 0.6vw, 15px);
    font-size: clamp(12px, 1vw, 16px);
    white-space: nowrap;
  }

  /* The circular Auto Complete and Refresh buttons are already small - they
     must not be squeezed any further. */
  .ant-btn-circle,
  .ant-btn-icon-only {
    flex: 0 0 auto;
  }

  .ant-input-search {
    flex: 0 1 auto;
    min-width: 0;
  }

  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }
`;

const ActiveTripsPill = styled.div`
  background: linear-gradient(135deg, #89339c, #b56ac8);
  color: white;
  padding: 12px clamp(12px, 1.3vw, 28px);
  border-radius: 9999px;
  font-size: clamp(13px, 1.05vw, 18px);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: clamp(6px, 0.9vw, 12px);
  white-space: nowrap;
  flex: 0 0 auto;

  /* The count is what anyone actually reads off the pill, so it runs a step
     larger than the "Active Trips :" label beside it. line-height:1 keeps the
     taller text inside the existing line box, so the pill does not grow. */
  .pill-value {
    font-size: clamp(16px, 1.3vw, 21px);
    line-height: 1;
  }

  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 16px;
    justify-content: center;
  }
`;

const Live: React.FC = () => {
  const dispatch = useAppDispatch();
  const { liveBookings, loading, error, tableLength, autoCompleteLoading, autoCompleteSuccess, autoCompleteError } =
    useAppSelector((state) => state.live);
  const { cancelData, loading: cancelLoading, error: cancelError, selectedCab, selectedCabError } = useAppSelector((state) => state.newBooking || {});
  const { success: completeSuccess, error: completeError, loading: completeLoading } = useAppSelector((state) => state.complete || {});
  const {
    editBooking, editBookingLoading, editBookingError,
    locations, vehicleTypes, tripTypes, goodsTypes,
    packages, intercityList, nightFare,
  } = useAppSelector((state) => state.booking);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  const [showFareDetails, setShowFareDetails] = useState(false);
  const [selectedFareDetail, setSelectedFareDetail] = useState<any>(null);
  const locationOptions = useLocationOptions();
  const { Option } = Select;
  const selectedBranch = useBranchId();
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const lastCancelDataRef = useRef<any>(null);
  const searchValueRef = useRef("");
  searchValueRef.current = searchValue;
  const today = dayjs();
  const [search, setSearch] = useState<any>("");
  const { updateBookingLoading, updateBookingSuccess, updateBookingError } =
    useAppSelector((state) => state.newBooking || {});
  const tableClass = "Live-table";
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState<number>(320);

  useEffect(() => {
    const measure = () => {
      const el = tableWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const headerH =
        el.querySelector<HTMLElement>(".ant-table-thead")?.offsetHeight ?? 55;
      const pager = el.querySelector<HTMLElement>(".ant-table-pagination");
      const pagerH = pager ? pager.offsetHeight + 32 : 64;
      const H_SCROLLBAR = 16;
      const BREATHING_ROOM = 12;
      setScrollY(
        Math.max(
          180,
          window.innerHeight - top - headerH - pagerH - H_SCROLLBAR - BREATHING_ROOM
        )
      );
    };

    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [loading]);

  const resolveFareType = (record: any): string => {
    if (!record) return "";
    if (record.faretype) return String(record.faretype);
    const trip = tripTypes?.find((t: any) => String(t.id) === String(record.triptype));
    return trip ? String(trip.name) : "";
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "Book ID",
      dataIndex: "id",
      key: "id",
      className: COPY_CELL,
      align: "center",
      width: 110,
      fixed: "left",
      onCell: (record: any) => {
        const statusClassMap: Record<number, string> = {
          0: "cell-status-assigned",
          1: "cell-status-accepted",
          2: "cell-status-arrived",
          3: "cell-status-started",
          4: "cell-status-completed",
        };
        return { className: statusClassMap[record.tripstatuscode] ?? "" };
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: COPY_CELL,
      align: "center",
      width: 150,
      fixed: "left",
    },
    {
      title: "Mobile",
      dataIndex: "mobileno",
      key: "mobileno",
      className: COPY_CELL,
      align: "center",
      width: 120,
      fixed: "left",
    },
    {
      title: "CabId",
      dataIndex: "cabid",
      key: "CabId",
      align: "center",
      fixed: "left",
      width: 120,
    },
    {
      title: "Date",
      dataIndex: "bookedtime",
      key: "Date",
      align: "center",
      fixed: "left",
      width: 120,
      render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Time",
      key: "time",
      align: "center",
      width: 150,
      render: (_: any, record: any) => {
        const rows = [
          { label: "B", value: record.bookedtime },
          { label: "A", value: record.assignedtime },
          { label: "R", value: record.reassigntime },
          { label: "S", value: record.tripstart },
          { label: "E", value: record.tripend },
        ].filter((r) => r.value);

        if (rows.length === 0) return "-";

        return (
          <div>
            {rows.map((r) => (
              <div key={r.label}>{r.label} - {dayjs(r.value).format("hh:mm A")}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: "From Location",
      dataIndex: "fromloc",
      key: "fromloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "To Location",
      dataIndex: "toloc",
      key: "toloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      align: "center",
      width: "auto",
    },
    {
      title: "Vehicle",
      dataIndex: "vehicletype",
      key: "vehicletype",
      align: "center",
      width: 110,
    },
    {
      title: "Fare Type",
      dataIndex: "faretype",
      key: "faretype",
      align: "center",
      width: "auto",
      render: (_: any, record: any) =>
        resolveFareType(record) || record.triptype || "-",
    },
    {
      title: "Credit",
      dataIndex: "iscredit",
      key: "credit",
      align: "center",
      width: 100,

      render: (val) => val ? "Yes" : "No",
    },
    {
      title: "Goods",
      dataIndex: "goods",
      key: "goods",
      align: "center",
      width: "auto",
    },
    {
      title: "OTP",
      dataIndex: "otp",
      key: "otp",
      align: "center",
      width: 130,
    },
    {
      title: "Booked By",
      dataIndex: "bookedby",
      key: "bookedby",
      align: "center",
      width: 150,
      render: (_: any, record: any) => {
        const rows = [
          { label: "B", value: record.bookedby },
          { label: "A", value: record.assignedby },
          { label: "R", value: record.reassignby },
          { label: "U", value: record.updatedby },
          { label: "E", value: record.enquiryby },
        ].filter((r) => r.value);

        if (rows.length === 0) return "-";

        return (
          <div>
            {rows.map((r) => (
              <div key={r.label}>{r.label} - {r.value}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleid",
      key: "vehicleid",
      align: "center",
      width: 130,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      width: 130,
    },
    {
      title: "Edit",
      key: "action",
      align: "center",
      width: 120,
      render: (_: any, record: any) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              disabled={editBookingLoading}
              onClick={() => handleEditBooking(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useMountEffectOnce(() => {
    dispatch(
      getLiveBookingsRequest({
        branchid: selectedBranch,
      })
    );
  });

  useEffect(() => {
    let refreshTimer: number | undefined;

    const handleBookingUpdated = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        dispatch(
          getLiveBookingsRequest({
            branchid: selectedBranch,
            filtervalue: searchValueRef.current || undefined,
          })
        );
      }, LIVE_SOCKET_REFRESH_DEBOUNCE_MS);
    };

    socket.on("booking_assigned", handleBookingUpdated);
    socket.on("trip_reassigned", handleBookingUpdated);
    socket.on("booking_cancelled", handleBookingUpdated);
    socket.on("booking_canceled", handleBookingUpdated);

    return () => {
      window.clearTimeout(refreshTimer);
      socket.off("booking_assigned", handleBookingUpdated);
      socket.off("trip_reassigned", handleBookingUpdated);
      socket.off("booking_cancelled", handleBookingUpdated);
      socket.off("booking_canceled", handleBookingUpdated);
    };
  }, [dispatch, selectedBranch]);

  useEffect(() => {
    if (autoCompleteSuccess) {
      Modal.success({ title: "Trip Completed", content: "Booking auto-completed successfully." });
      setSelectedRowKey(null);
      dispatch(getLiveBookingsRequest({ branchid: selectedBranch, filtervalue: searchValue || undefined }));
      dispatch(autoCompleteReset());
    }
  }, [autoCompleteSuccess, dispatch, searchValue, selectedBranch]);

  useEffect(() => {
    if (autoCompleteError) {
      const isFareLimit = autoCompleteError.status === 400 && autoCompleteError.data?.fare;

      if (isFareLimit) {
        Modal.warning({
          title: "Fare Limit Exceeded",
          content: `Fare (Rs.${autoCompleteError.data.fare}) exceeds Rs.800. Distance: ${autoCompleteError.data.distance} km, ${autoCompleteError.data.actualMinutes} min. Please complete this trip manually.`,
        });
      } else {
        Modal.error({
          title: "Auto Complete Failed",
          content: autoCompleteError.message || "Something went wrong.",
        });
      }

      dispatch(autoCompleteReset());
    }
  }, [autoCompleteError, dispatch]);

  useEffect(() => {
    if (completeSuccess) {
      Modal.success({
        title: "Trip Completed",
        content: "Booking completed successfully.",
      });
      setSelectedRowKey(null);
      dispatch(getLiveBookingsRequest({ branchid: selectedBranch, filtervalue: searchValue || undefined }));
      dispatch(resetCompleteTrip());
    }
  }, [completeSuccess, dispatch, searchValue, selectedBranch]);

  useEffect(() => {
    if (completeError) {
      Modal.error({
        title: "Complete Trip Failed",
        content: completeError || "Unable to complete the trip.",
      });
      dispatch(resetCompleteTrip());
    }
  }, [completeError, dispatch]);

  useEffect(() => {
    if (cancelData && lastCancelDataRef.current !== cancelData) {
      lastCancelDataRef.current = cancelData;
      message.success("Booking cancelled successfully");
      setSelectedRowKey(null);
      setCancelModalOpen(false);
      dispatch(
        getLiveBookingsRequest({
          branchid: selectedBranch,
          filtervalue: searchValue || undefined,
        })
      );
    }
  }, [cancelData, dispatch, searchValue, selectedBranch]);

  useEffect(() => {
    if (cancelError) {
      Modal.error({
        title: "Cancel Failed",
        content: typeof cancelError === "string" ? cancelError : "Unable to cancel booking.",
      });
    }
  }, [cancelError]);

  useEffect(() => {
    if (selectedCab?.cabid) {
      setReassignModalOpen(true);
    }
  }, [selectedCab]);

  useEffect(() => {
    if (selectedCabError) {
      message.error(selectedCabError);
    }
  }, [selectedCabError]);

  useEffect(() => {
    if (editModalOpen) {
      if (!locations?.length) dispatch(getLocationsRequest(selectedBranch));
      if (!vehicleTypes?.length) dispatch(getVehicleTypesRequest(selectedBranch));
      if (!tripTypes?.length) dispatch(getTripTypesRequest());
      if (!goodsTypes?.length) dispatch(getGoodsTypesRequest());
    }
  }, [editModalOpen]);

  useEffect(() => {
    if (editBooking) {
      const dateTime = dayjs(editBooking.dateandtime);
      const tripTypeVal = String(editBooking.triptype) || undefined;
      const vehicleIdVal = String(editBooking.vehicleid) || undefined;

      const fromLoc = (locations || []).find((l: any) => l.PlaceName === editBooking.fromloc);
      const toLoc = (locations || []).find((l: any) => l.PlaceName === editBooking.toloc);
      const goodsMatch = (goodsTypes || []).find((g: any) => g.goodsid === editBooking.goodsid);

      editForm.setFieldsValue({
        customername: editBooking.customername || "",
        mobileno: editBooking.mobileno || "",
        fromloc: editBooking.fromloc || "",
        toloc: editBooking.toloc || "",
        fromlat: editBooking.fromlat || fromLoc?.Latitude || undefined,
        fromlong: editBooking.fromlong || fromLoc?.Longitude || undefined,
        tolat: editBooking.tolat || toLoc?.Latitude || undefined,
        tolong: editBooking.tolong || toLoc?.Longitude || undefined,
        tripdate: dateTime.isValid() ? dateTime : dayjs(),
        triptime: dateTime.isValid() ? dateTime : dayjs(),
        vehicleid: vehicleIdVal,
        triptype: tripTypeVal,
        goodstype: goodsMatch?.goodsname || undefined,
        distance: editBooking.distance || "",
        alternativemobileno: editBooking.alternativemobileno || "",
        remarks: editBooking.remarks || "",
        // fareDetails:
        //   editBooking.packagetype ||
        //   editBooking.intercitytype ||
        //   editBooking.nighttype ||
        //   undefined,
        features: [
          isFlagOn(editBooking.iscv) ? "CV" : null,
          isFlagOn(editBooking.isgst) ? "GST" : null,
          isFlagOn(editBooking.istollgate) ? "Toll Gate" : null,
          isFlagOn(editBooking.iscredit) ? "Prepaid" : null,
        ].filter(Boolean),
      });
      const numTripType = Number(tripTypeVal);
      if ([2, 3, 4].includes(numTripType) && vehicleIdVal) {
        setShowFareDetails(true);
        switch (numTripType) {
          case 2: dispatch(getPackagesRequest(vehicleIdVal, selectedBranch)); break;
          case 3: dispatch(getIntercityListRequest(vehicleIdVal, selectedBranch)); break;
          case 4: dispatch(getNightFareRequest(vehicleIdVal, selectedBranch)); break;
        }
      } else {
        setShowFareDetails(false);
      }
    }
  }, [editBooking, editForm, locations]);

  useEffect(() => {
    if (editBooking?.goodsid && goodsTypes?.length) {
      const goodsMatch = goodsTypes.find((g: any) => g.goodsid === editBooking.goodsid);
      if (goodsMatch) {
        editForm.setFieldsValue({ goodstype: goodsMatch.goodsname });
      }
    }
  }, [goodsTypes, editBooking, editForm]);

  useEffect(() => {
    if (!editBooking) return;
    const tripTypeVal = Number(editBooking.triptype);

    if (tripTypeVal === 3 && intercityList?.length) {
      const target = String(editBooking.intercitytype || "").trim();
      const match = intercityList.find((ic: any) => {
        const label = `${ic.basekm}-${ic.basefare} - ${convertMinutesToHrs(ic.baseminute)}`;
        return label.trim() === target;
      });
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.tollerenckm });
        setSelectedFareDetail(match);
      }
    } else if (tripTypeVal === 2 && packages?.length) {
      const target = String(editBooking.packagetype || "").trim();
      //console.log("PACKAGE TARGET:", target);
      //console.log("PACKAGES LIST:", packages);
      const match = packages.find((pkg: any) => String(pkg.package || "").trim() === target);
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.package });
        setSelectedFareDetail(match);
      }
    } else if (tripTypeVal === 4 && nightFare?.length) {
      const target = String(editBooking.nighttype || "").trim();
      const match = nightFare.find((nf: any) => String(nf.package || "").trim() === target);
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.package });
        setSelectedFareDetail(match);
      }
    }
  }, [editBooking, intercityList, packages, nightFare, editForm]);

  useEffect(() => {
    if (updateBookingSuccess) {
      message.success("Booking updated successfully!");
      setEditModalOpen(false);
      editForm.resetFields();
      dispatch({ type: "UPDATE_NEW_BOOKING_RESET" });
      dispatch(getNewBookingEditReset());
      dispatch(getLiveBookingsRequest({ branchid: selectedBranch, filtervalue: searchValue || undefined }));
    } else if (updateBookingError) {
      message.error(updateBookingError);
      dispatch({ type: "UPDATE_NEW_BOOKING_RESET" });
    }
  }, [updateBookingSuccess, updateBookingError]);

  const handleEditBooking = (record: any) => {
    const bookingId = record.id || record.BookId || record.tripid;
    if (!bookingId) {
      message.warning("Booking ID not found.");
      return;
    }
    setEditingBookingId(String(bookingId));
    dispatch(getNewBookingEditRequest(String(bookingId)));
    setEditModalOpen(true);
  };

  const rowSelection = {
    type: "radio" as const,
    selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
    onChange: (keys: React.Key[]) => {
      setSelectedRowKey(keys[0]?.toString() || null);
    },
  };
  const selectedBooking = liveBookings.find(
    (b: any) =>
      String(b.id) === selectedRowKey ||
      String(b.BookId) === selectedRowKey
  ) || null;

  const isMeterFare = resolveFareType(selectedBooking).toLowerCase().includes("meter");
  const canAutoComplete = Boolean(selectedBooking) && isMeterFare;

  const isValidCoordinate = (value: any) => {
    return value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));
  };

  const handleAutoComplete = () => {
    if (!selectedBooking) {
      Modal.warning({
        title: "Select Booking",
        content: "Please select a booking first.",
      });
      return;
    }

    const bookingId =
      selectedBooking.id ||
      selectedBooking.BookId ||
      selectedBooking.bookid ||
      selectedBooking.bookingId ||
      selectedBooking._id;
    const cabId =
      selectedBooking.cabId ||
      selectedBooking.cabid ||
      selectedBooking.vehicleid ||
      selectedBooking.cabno;

    const piclat = isValidCoordinate(selectedBooking.toloclat)
      ? selectedBooking.toloclat
      : isValidCoordinate(selectedBooking.fromloclat)
        ? selectedBooking.fromloclat
        : null;
    const piclon = isValidCoordinate(selectedBooking.toloclong)
      ? selectedBooking.toloclong
      : isValidCoordinate(selectedBooking.fromloclong)
        ? selectedBooking.fromloclong
        : null;

    if (!bookingId || !cabId) {
      Modal.warning({
        title: "Invalid Booking",
        content: "Selected booking does not contain a valid booking ID or cab ID.",
      });
      return;
    }

    dispatch(
      autoCompleteRequest({
        bookid: bookingId,
        bookingid: bookingId,
        bookingId: bookingId,
        cabid: cabId,
        cabId: cabId,
        iscredit: selectedBooking.iscredit,
        feedback: "",
        completedby: getEmployeeName(),
        piclat,
        piclon,
        present_location: selectedBooking.toloc || selectedBooking.fromloc || "",
      })
    );
  };

  const filteredBookings = liveBookings.filter((booking: DataType) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return (
      booking.id?.toString().toLowerCase().includes(lower) ||
      booking.mobileno?.toString().toLowerCase().includes(lower) ||
      booking.name?.toString().toLowerCase().includes(lower) ||
      booking.cabid?.toString().toLowerCase().includes(lower)
    );
  });

  const convertMinutesToHrs = (minutes: number): string => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}.${mins === 0 ? "00" : mins} Hrs`;
  };

  const handleEditFareTypeChange = (value: any) => {
    const vehicleId = editForm.getFieldValue("vehicleid");
    setShowFareDetails([2, 3, 4].includes(Number(value)));
    editForm.setFieldsValue({ fareDetails: undefined });
    setSelectedFareDetail(null);
    if (!vehicleId) return;
    if (!value) { dispatch(resetFareEstimate()); return; }
    switch (Number(value)) {
      case 2: dispatch(getPackagesRequest(vehicleId, selectedBranch)); break;
      case 3: dispatch(getIntercityListRequest(vehicleId, selectedBranch)); break;
      case 4: dispatch(getNightFareRequest(vehicleId, selectedBranch)); break;
    }
  };

  const PILL_OPTIONS = ["CV", "Toll Gate", "Prepaid"];

  const PillGroup: React.FC<{ value?: string[]; onChange?: (v: string[]) => void }> = ({ value = [], onChange }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {PILL_OPTIONS.map((opt) => {
        const active = value.includes(opt);
        return (
          <div
            key={opt}
            onClick={() => onChange?.(active ? value.filter((v) => v !== opt) : [...value, opt])}
            style={{
              padding: "6px 16px",
              borderRadius: 9999,
              fontSize: 13,
              cursor: "pointer",
              userSelect: "none",
              border: `1px solid ${active ? "#89339c" : "#e0e0e0"}`,
              background: active ? "linear-gradient(135deg, #89339c, #b56ac8)" : "#fafafa",
              color: active ? "#fff" : "#666",
              transition: "all .15s",
            }}
          >
            {opt}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={pageWrapper}>
      <Toolbar>
        <ToolbarGroup>
          {/* {canAutoComplete && (
            <Tooltip title="Auto Complete">
              <Button
                type="primary"
                shape="circle"
                icon={<FileDoneOutlined />}
                style={{
                  background: "#52c41a",
                  borderColor: "#52c41a",
                }}
                loading={autoCompleteLoading}
                disabled={loading || autoCompleteLoading}
                onClick={handleAutoComplete}
              />
            </Tooltip>
          )} */}

          {/* <ReassignModel
            reassignOpen={reassignModalOpen}
            setReassignOpen={setReassignModalOpen}
            selectedRowKey={selectedRowKey}
            selectedBookingData={selectedBooking}
          /> */}

          {/* <CompleteModel
            selectedRowKey={selectedRowKey}
            selectedBookingData={selectedBooking}

            onCompleteConfirm={(rowKey, values) => {
              if (!selectedBooking) {
                Modal.warning({
                  title: "Select Booking",
                  content: "Please select a booking first.",
                });
                return;
              }

              const bookingId =
                selectedBooking.id ||
                selectedBooking.BookId ||
                selectedBooking.bookid ||
                selectedBooking.bookingId ||
                selectedBooking._id;
              const cabId =
                selectedBooking.cabId ||
                selectedBooking.cabid ||
                selectedBooking.vehicleid ||
                selectedBooking.cabno;

              const piclat = isValidCoordinate(selectedBooking.toloclat)
                ? selectedBooking.toloclat
                : isValidCoordinate(selectedBooking.fromloclat)
                  ? selectedBooking.fromloclat
                  : null;
              const piclon = isValidCoordinate(selectedBooking.toloclong)
                ? selectedBooking.toloclong
                : isValidCoordinate(selectedBooking.fromloclong)
                  ? selectedBooking.fromloclong
                  : null;

              dispatch(
                completeTripRequest({
                  bookingid: bookingId,
                  cabid: cabId,
                  distance: values.distance || selectedBooking.distance || 0,
                  mins: values.mins || values.time,
                  fare: values.fare,
                  present_location: values.location || selectedBooking.toloc || selectedBooking.fromloc || "",
                  feedback: values.feedback || "",
                  completedby: getEmployeeName(),
                  iscredit: selectedBooking.iscredit,
                  piclat,
                  piclon,
                })
              );
            }}
          /> */}

          {/* <CancelAssign
            selectedRowKey={selectedRowKey}
            selectedBookingData={selectedBooking}
            isOpen={isCancelModalOpen}
            onOpenChange={setCancelModalOpen}
            confirmLoading={cancelLoading}
            onCancelConfirm={(rowKey, values) => {
              dispatch(
                cancelBookingRequest({
                  bookingid: Number(values.bookingId),
                  cancelreason: values.reason,
                  cancelledby: getEmployeeName(),
                  canceltype: 2,
                  cabid: values.cabId || selectedBooking?.cabid || selectedBooking?.cabId || undefined,
                  remarks: values.remarks || "",
                })
              );
            }}
          /> */}

          {/* <Search
            disabled={!selectedBooking}
            placeholder="Cab_ID Search.."
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: "clamp(120px, 11vw, 240px)" }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={(value) => {
              if (!selectedBooking) return;

              const fromlat = selectedBooking.fromloclat;
              const fromlong = selectedBooking.fromloclong;

              if (!fromlat || !fromlong) {
                message.warning("Pickup location not found.");
                return;
              }

              dispatch(
                getSelectedCabRequest({
                  cabid: value.trim(),
                  fromlat,
                  fromlong,
                })
              );
            }}
          /> */}

          <Tooltip title="Refresh">
            <Button
              icon={<RedoOutlined />}
              style={refreshBtn}
              onClick={() => {
                setSearch("");
                setSearchValue("");
                setSelectedRowKey(null);
                dispatch(
                  getLiveBookingsRequest({
                    branchid: selectedBranch,
                  })
                );
              }}
            />
          </Tooltip>
        </ToolbarGroup>

        <ToolbarGroup>
          <ActiveTripsPill>
            <CarOutlined />
            Active Trips : <span className="pill-value">{tableLength}</span>
          </ActiveTripsPill>

          <Search
            placeholder="Search bookings..."
            enterButton={<SearchOutlined />}
            value={search}
            size="large"
            allowClear
            style={{ width: "clamp(140px, 13vw, 280px)" }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </ToolbarGroup>
      </Toolbar>

      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <Table<DataType>
          bordered
          className={tableClass}
          rowSelection={rowSelection}
          rowClassName={(record) => {
            const v = record.vehicletype?.toLowerCase() || "";
            if (v.includes("bolero")) return "bolero-row";
            if (v.includes("407")) return "row-407";
            if (v.includes("eicher")) return "row-eicher";
            return "";
          }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedRowKey(record.key.toString());
            },
          })}
          columns={columns}
          dataSource={filteredBookings.map((b: any, i: number) => ({ ...b, key: String(b.id || b.BookId || b._id || i) }))}
          loading={loading}
          rowKey="key"
          scroll={{
            x: 2600,
            y: scrollY,
          }}
          pagination={{
            pageSizeOptions: ["5", "10", "100"],
            showSizeChanger: true,
            defaultPageSize: 100,
          }}
        />
      </div>

      <Modal
        open={editModalOpen}
        destroyOnHidden
        onCancel={() => {
          setEditModalOpen(false);
          dispatch(getNewBookingEditReset());
          editForm.resetFields();
        }}
        footer={null}
        width={{ xs: "94vw", md: 680 }}
        styles={{ body: { padding: 0, background: " rgb(218 183 228)", borderRadius: "20px" } }}
        title={
          <div style={{
            background: "linear-gradient(135deg, #89339c, #b56ac8)",
            margin: "-20px -24px 0",
            padding: "18px 24px",
            borderRadius: "8px 8px 0 0",
            overflow: "hidden",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, background: "rgba(255,255,255,0.18)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EditOutlined style={{ color: "#fff", fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>Edit Booking</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, marginTop: 2 }}>
                    {editBooking?.id || selectedRowKey}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        {editBookingLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Loading booking details..." />
          </div>
        ) : editBookingError ? (
          <div style={{ textAlign: "center", padding: 24, color: "red" }}>{editBookingError}</div>
        ) : (
          <Form form={editForm} layout="vertical" onFinish={(values) => {
            const features = values.features || [];
            const iscv = features.includes("CV");
            const istollgate = features.includes("Toll Gate");
            const isgst = features.includes("GST");
            const iscredit = features.includes("Prepaid");
            const fareType = Number(values.triptype);

            const intercityLabel = fareType === 3 && selectedFareDetail
              ? `${selectedFareDetail.basekm}-${selectedFareDetail.basefare} - ${convertMinutesToHrs(selectedFareDetail.baseminute)}`
              : values.fareDetails;

            dispatch(updateNewBookingRequest({
              bookid: editBooking?.id || editingBookingId,
              updatedby: getEmployeeName() || "Admin",
              branchid: selectedBranch,
              mobileno: values.mobileno,
              name: values.customername,
              fromloc: values.fromloc,
              toloc: values.toloc,
              fromlat: values.fromlat,
              fromlong: values.fromlong,
              tolat: values.tolat,
              tolong: values.tolong,
              triptype: String(values.triptype),
              vechicletype: Number(values.vehicleid),
              goodstype: values.goodstype,
              remark: values.remarks,
              alternativemobno: values.alternativemobileno,
              distance: values.distance,
              fare: "0",
              tripdate: values.tripdate?.format("YYYY-MM-DD"),
              triptime: values.triptime?.format("HH:mm:ss"),
              packagetype: fareType === 2 ? values.fareDetails : undefined,
              intercitytype: fareType === 3 ? intercityLabel : undefined,
              nighttype: fareType === 4 ? values.fareDetails : undefined,
              iscredit,
              iscv,
              istollgate,
              isgst,
              noofvechicles: 1,
            }));
          }}
            style={{ padding: "14px 22px 0" }}>

            <Form.Item name="fromlat" hidden><Input /></Form.Item>
            <Form.Item name="fromlong" hidden><Input /></Form.Item>
            <Form.Item name="tolat" hidden><Input /></Form.Item>
            <Form.Item name="tolong" hidden><Input /></Form.Item>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Customer info
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="Customer name" name="customername" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Input style={{ height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Mobile number"
                    name="mobileno"
                    rules={[
                      { required: true, message: "Mobile number is required" },
                      { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input maxLength={10} style={{ height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Alternative mobile"
                    name="alternativemobileno"
                    rules={[{ pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input maxLength={10} style={{ height: 36 }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Trip details
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="From location" name="fromloc" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <LocationSelect
                      placeholder="From location"
                      style={{ height: 36 }}
                      options={locationOptions}
                      onPicked={(value) => {
                        const loc = (locations || []).find((l: any) => l.PlaceName === value);
                        editForm.setFieldsValue({
                          fromlat: loc?.Latitude ? String(loc.Latitude) : undefined,
                          fromlong: loc?.Longitude ? String(loc.Longitude) : undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="To location" name="toloc" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <LocationSelect
                      placeholder="To location"
                      style={{ height: 36 }}
                      options={locationOptions}
                      onPicked={(value) => {
                        const loc = (locations || []).find((l: any) => l.PlaceName === value);
                        editForm.setFieldsValue({
                          tolat: loc?.Latitude ? String(loc.Latitude) : undefined,
                          tolong: loc?.Longitude ? String(loc.Longitude) : undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Date" name="tripdate" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <DatePicker format="DD-MM-YYYY" style={{ width: "100%", height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Time" name="triptime" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <TimePicker format="hh:mm A" use12Hours style={{ width: "100%", height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
                    <Input style={{ height: 36 }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Vehicle & options
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="Vehicle type" name="vehicleid" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Select
                      style={{ height: 36 }}
                      onChange={(val) => {
                        editForm.setFieldsValue({ fareDetails: undefined });
                        setSelectedFareDetail(null);
                        dispatch(resetFareEstimate());
                        dispatch(clearFareLists());
                        const currentFareType = Number(editForm.getFieldValue("triptype"));
                        if ([2, 3, 4].includes(currentFareType)) {
                          setShowFareDetails(true);
                          switch (currentFareType) {
                            case 2: dispatch(getPackagesRequest(val, selectedBranch)); break;
                            case 3: dispatch(getIntercityListRequest(val, selectedBranch)); break;
                            case 4: dispatch(getNightFareRequest(val, selectedBranch)); break;
                          }
                        }
                      }}
                    >
                      {vehicleTypes?.map((v: any) => <Option key={v.id} value={String(v.id)}>{v.vehicletype}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Goods type" name="goodstype" style={{ marginBottom: 0 }}>
                    <Select allowClear showSearch optionFilterProp="children" style={{ height: 36 }}>
                      {(goodsTypes || []).map((g: any) => (
                        <Option key={g.goodsid} value={g.goodsname}>{g.goodsname}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Fare type" name="triptype" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Select
                      style={{ height: 36 }}
                      allowClear
                      onChange={(value) => handleEditFareTypeChange(value)}
                    >
                      {tripTypes?.map((t: any) => <Option key={t.id} value={String(t.id)}>{t.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                {/* {showFareDetails && (
                  <Col span={12}>
                    <Form.Item label="Fare details" name="fareDetails" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                      <Select
                        style={{ height: 36 }}
                        placeholder="Select details"
                        onChange={(value) => {
                          const fareType = Number(editForm.getFieldValue("triptype"));
                          let selectedItem = null;
                          if (fareType === 2) {
                            selectedItem = packages?.find((pkg: any) => pkg.package === value);
                          } else if (fareType === 3) {
                            selectedItem = intercityList?.find((ic: any) => ic.tollerenckm === value);
                          } else if (fareType === 4) {
                            selectedItem = nightFare?.find((nf: any) => nf.package === value);
                          }
                          setSelectedFareDetail(selectedItem);
                        }}
                      >
                        {Number(editForm.getFieldValue("triptype")) === 3 ? (
                          intercityList?.map((ic: any) => (
                            <Option key={ic.tollerenckm} value={ic.tollerenckm}>
                              {`${ic.basekm}-${ic.basefare} - ${convertMinutesToHrs(ic.baseminute)}`}
                            </Option>
                          ))
                        ) : (
                          (Number(editForm.getFieldValue("triptype")) === 2 ? packages : nightFare)?.map((pkg: any) => (
                            <Option key={pkg.id} value={pkg.package}>{pkg.package}</Option>
                          ))
                        )}
                      </Select>
                    </Form.Item>
                  </Col>
                )} */}
                <Col span={24}>
                  <Form.Item label="Add-ons" name="features" style={{ marginBottom: 0 }}>
                    <PillGroup />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 10, padding: "14px 0 16px", borderTop: "0.5px solid #f0f0f0", marginTop: 4 }}>
              <Button type="primary" htmlType="submit" style={{
                height: 36, minWidth: 140, borderRadius: 8, fontWeight: 500, border: "none",
                background: "linear-gradient(135deg, #89339c, #b56ac8)"
              }}
                loading={updateBookingLoading}>
                Update booking
              </Button>

              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  dispatch(getNewBookingEditReset());
                  editForm.resetFields();
                }}
                style={{ height: 36, borderRadius: 8, backgroundColor: "#f02929ce", color: "white" }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default Live;